import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';
import * as QRCode from 'qrcode';

import { AuthService } from '../../core/auth/auth.service';
import { AiConsentState, BusinessAiConsent } from '../../core/attestations/ai-consent.types';
import { BusinessAiConsentService } from '../../core/attestations/business-ai-consent.service';
import { AiConsentCheckboxComponent } from '../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { AiConsentWithdrawComponent } from '../../shared/ai-consent/ai-consent-withdraw/ai-consent-withdraw';
import { PhoneInputComponent } from '../../shared/phone-input/phone-input';
import { IconComponent } from '../../shared/icon/icon';
import { GENERIC_ERROR } from '../../core/http/read-error';

/** Which step of the 2FA enrolment the security card is showing. */
type TwoFactorStep = 'idle' | 'setup' | 'confirming';

@Component({
  selector: 'app-profile',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    PhoneInputComponent,
    IconComponent,
    DatePipe,
    AiConsentCheckboxComponent,
    AiConsentWithdrawComponent,
  ],
  templateUrl: './profile.html',
  styleUrl: './profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly email = signal('');
  protected readonly loaded = signal(false);
  protected readonly savingProfile = signal(false);
  protected readonly profileSaved = signal(false);
  protected readonly profileError = signal<string | null>(null);

  // --- 2FA state ---
  protected readonly twoFactorEnabled = signal(false);
  protected readonly twoFactorStep = signal<TwoFactorStep>('idle');
  protected readonly twoFactorSecret = signal('');
  protected readonly qrDataUrl = signal('');
  protected readonly twoFactorBusy = signal(false);
  protected readonly twoFactorError = signal<string | null>(null);
  protected readonly secretCopied = signal(false);

  // --- AI-assisted assessment consent (POPIA s72, the standing sign-up one) ---
  /** State + timestamps of the standing consent; null if never given. */
  protected readonly aiConsent = signal<AiConsentState | null>(null);
  protected readonly aiConsentActive = computed(() => this.aiConsent()?.state === 'ACTIVE');
  protected readonly aiConsentTick = signal(false);
  protected readonly aiConsentBusy = signal(false);
  protected readonly aiConsentError = signal<string | null>(null);

  /**
   * The withdrawal POST for the shared control, as a factory so nothing fires
   * until the person confirms. The server cascades it to every live
   * per-application consent, so the confirm copy says every application.
   */
  protected readonly withdrawAiConsent = () =>
    this.authService.withdrawAiConsent().pipe(tap((p) => this.aiConsent.set(p.ai_consent)));

  protected readonly withdrawMessage =
    'Uploads and AI assessment stop for every one of your applications until you consent again.';

  // --- FLOW-297: the consent is each BUSINESS's (`§A6.7.2`) ---
  /** One entry per business this person may act for; empty for none. */
  protected readonly businessConsents = signal<BusinessAiConsent[]>([]);
  /** Entity short_ids whose box is ticked, awaiting Save. */
  protected readonly businessTicks = signal<ReadonlySet<string>>(new Set());
  private readonly businessConsent = inject(BusinessAiConsentService);

  private readonly withdrawers = new Map<string, () => Observable<unknown>>();

  /**
   * The withdrawal POST for one business, as a factory for the shared control.
   * Cached per business: a new identity each render would churn the input.
   */
  protected withdrawFor(entityShortId: string): () => Observable<unknown> {
    let fn = this.withdrawers.get(entityShortId);
    if (!fn) {
      fn = () =>
        this.businessConsent
          .withdraw(entityShortId)
          .pipe(tap((r) => this.setBusinessState(entityShortId, r.ai_consent)));
      this.withdrawers.set(entityShortId, fn);
    }
    return fn;
  }

  protected tickBusiness(entityShortId: string, ticked: boolean): void {
    const next = new Set(this.businessTicks());
    if (ticked) next.add(entityShortId);
    else next.delete(entityShortId);
    this.businessTicks.set(next);
  }

  protected giveForBusiness(entityShortId: string): void {
    if (this.aiConsentBusy() || !this.businessTicks().has(entityShortId)) return;
    this.aiConsentError.set(null);
    this.aiConsentBusy.set(true);
    this.businessConsent
      .give(entityShortId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.aiConsentBusy.set(false);
          this.tickBusiness(entityShortId, false);
          this.setBusinessState(entityShortId, r.ai_consent);
        },
        error: () => {
          this.aiConsentBusy.set(false);
          this.aiConsentError.set('We could not record the consent. Please try again.');
        },
      });
  }

  private setBusinessState(entityShortId: string, state: AiConsentState | null): void {
    this.businessConsents.update((list) =>
      list.map((b) => (b.entity_short_id === entityShortId ? { ...b, ai_consent: state } : b)),
    );
  }

  protected readonly form = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    cellNumber: this.fb.control<string | null>(null),
    jobPosition: [''],
    smsNotification: [true],
    emailNotification: [true],
  });

  protected readonly confirmForm = this.fb.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  protected readonly disableForm = this.fb.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  protected get firstNameControl() {
    return this.form.controls.firstName;
  }
  protected get lastNameControl() {
    return this.form.controls.lastName;
  }

  ngOnInit(): void {
    this.authService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          this.email.set(p.email);
          this.twoFactorEnabled.set(p.two_factor_enabled);
          this.aiConsent.set(p.ai_consent ?? null);
          this.businessConsents.set(p.business_ai_consents ?? []);
          this.form.patchValue({
            firstName: p.first_name,
            lastName: p.last_name,
            cellNumber: p.cell_number,
            jobPosition: p.job_position ?? '',
            smsNotification: p.sms_notification,
            emailNotification: p.email_notification,
          });
          this.loaded.set(true);
        },
        error: () => {
          this.profileError.set('We could not load your profile. Please refresh.');
          this.loaded.set(true);
        },
      });
  }

  protected saveProfile(): void {
    this.form.markAllAsTouched();
    if (this.savingProfile() || this.form.invalid) return;

    this.profileError.set(null);
    this.profileSaved.set(false);
    this.savingProfile.set(true);

    const v = this.form.getRawValue();
    this.authService
      .updateProfile({
        first_name: v.firstName!,
        last_name: v.lastName!,
        cell_number: v.cellNumber ?? '',
        job_position: v.jobPosition ?? '',
        sms_notification: v.smsNotification!,
        email_notification: v.emailNotification!,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingProfile.set(false);
          this.profileSaved.set(true);
        },
        error: () => {
          this.savingProfile.set(false);
          this.profileError.set('We could not save your changes. Please try again.');
        },
      });
  }

  // --- AI-assisted assessment consent ---

  /** Give the standing consent (an older account, or again after a withdrawal). */
  protected giveAiConsent(): void {
    if (this.aiConsentBusy() || !this.aiConsentTick()) return;
    this.aiConsentError.set(null);
    this.aiConsentBusy.set(true);
    this.authService
      .giveAiConsent()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          this.aiConsentBusy.set(false);
          this.aiConsentTick.set(false);
          this.aiConsent.set(p.ai_consent);
        },
        error: () => {
          this.aiConsentBusy.set(false);
          this.aiConsentError.set('We could not record your consent. Please try again.');
        },
      });
  }

  // --- Two-factor enrolment ---

  protected startTwoFactor(): void {
    this.twoFactorError.set(null);
    this.twoFactorBusy.set(true);
    this.authService
      .startTwoFactorSetup()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (setup) => {
          this.twoFactorSecret.set(setup.secret);
          this.twoFactorStep.set('setup');
          void this.renderQr(setup.otpauth_uri);
          this.twoFactorBusy.set(false);
        },
        error: () => {
          this.twoFactorBusy.set(false);
          this.twoFactorError.set('We could not start setup. Please try again.');
        },
      });
  }

  protected confirmTwoFactor(): void {
    this.confirmForm.markAllAsTouched();
    if (this.twoFactorBusy() || this.confirmForm.invalid) return;

    this.twoFactorError.set(null);
    this.twoFactorBusy.set(true);
    this.authService
      .confirmTwoFactor(this.confirmForm.controls.code.value!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.twoFactorBusy.set(false);
          this.twoFactorEnabled.set(true);
          this.resetTwoFactorSetup();
        },
        error: (err: unknown) => {
          this.twoFactorBusy.set(false);
          this.twoFactorError.set(this.mapTwoFactorError(err));
        },
      });
  }

  protected disableTwoFactor(): void {
    this.disableForm.markAllAsTouched();
    if (this.twoFactorBusy() || this.disableForm.invalid) return;

    this.twoFactorError.set(null);
    this.twoFactorBusy.set(true);
    this.authService
      .disableTwoFactor({ code: this.disableForm.controls.code.value! })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.twoFactorBusy.set(false);
          this.twoFactorEnabled.set(false);
          this.disableForm.reset();
        },
        error: (err: unknown) => {
          this.twoFactorBusy.set(false);
          this.twoFactorError.set(this.mapTwoFactorError(err));
        },
      });
  }

  protected cancelTwoFactor(): void {
    this.resetTwoFactorSetup();
  }

  protected copySecret(): void {
    void navigator.clipboard?.writeText(this.twoFactorSecret()).then(() => {
      this.secretCopied.set(true);
      setTimeout(() => this.secretCopied.set(false), 2000);
    });
  }

  private resetTwoFactorSetup(): void {
    this.twoFactorStep.set('idle');
    this.twoFactorSecret.set('');
    this.qrDataUrl.set('');
    this.confirmForm.reset();
    this.twoFactorError.set(null);
  }

  private async renderQr(uri: string): Promise<void> {
    try {
      const url = await QRCode.toDataURL(uri, { margin: 1, width: 220 });
      this.qrDataUrl.set(url);
    } catch {
      // QR rendering failed — the manual secret entry below is the fallback.
      this.qrDataUrl.set('');
    }
  }

  private mapTwoFactorError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const code = (err.error as { code?: string } | null)?.code;
      if (code === 'TOTP_INVALID') {
        return 'That code is incorrect or has expired. Try the current code.';
      }
    }
    return GENERIC_ERROR;
  }
}
